import { inject } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { ClientsService } from '@core/services/clients/clients.service';
import { ClientConfigService } from '../client-config/client-config.service';
import { ClientContextPersistenceService } from '../client-context-persistence/client-context-persistence.service';
import { LoaderService } from '../loader/loader.service';
import { LoggerService } from '../logger/logger.service';
import { PageVisitTrackingService } from '../analytics/page-visit-tracking.service';
import { catchError, forkJoin, map, of, retry, switchMap, throwError, timer } from 'rxjs';
import { ManifestService } from '../manifest/manifest.service';

/** Errores 4xx (slug real/definitivo) no se reintentan; solo blips de red/5xx. */
const RETRY_DELAY_MS = 300;
const MAX_RETRIES = 2;

export const clientSetupResolver: ResolveFn<boolean> = (route, state) => {
  const clientsService = inject(ClientsService);
  const clientConfigService = inject(ClientConfigService);
  const clientContextPersistence = inject(ClientContextPersistenceService);
  const router = inject(Router);
  const loaderService = inject(LoaderService);
  const logger = inject(LoggerService).forContext('ClientSetupResolver');
  const pageVisitTracking = inject(PageVisitTrackingService);
  const manifestService = inject(ManifestService);

  const slug = route.paramMap.get('client');

  loaderService.setLoading(true);

  if (!slug) {
    loaderService.setLoading(false);
    router.navigate(['']);
    return of(false);
  }

  const cached = clientContextPersistence.get();
  if (cached?.slug === slug) {
    clientConfigService.setConfig(cached.config);
    manifestService.setClientManifest(slug);
    loaderService.setLoading(false);
    return of(true);
  }

  return clientsService.validateWebConfig(slug).pipe(
    retry({
      count: MAX_RETRIES,
      delay: (error: HttpErrorResponse) =>
        error.status >= 400 && error.status < 500 ? throwError(() => error) : timer(RETRY_DELAY_MS),
    }),
    switchMap((response) => {
      if (response.exists && response.is_active) {
        const clientId = response.config!.client_id;

        // Llamar a ambos endpoints en paralelo con forkJoin
        return forkJoin({
          address: clientsService.getClientShippingAddress(slug).pipe(
            catchError((addressError) => {
              logger.warn('Error fetching client shipping address:', addressError);
              return of(null);
            }),
          ),
          configStatus: clientsService.getConfigurationStatus(clientId).pipe(
            catchError((statusError) => {
              logger.warn('Error fetching configuration status:', statusError);
              // En caso de error, retornar estado con error=true (fail-closed)
              return of({
                client_id: clientId,
                has_transport_companies: false,
                has_payment_methods: false,
                active_payment_methods: [],
                error: true,
              });
            }),
          ),
        }).pipe(
          map(({ address, configStatus }) => {
            // Crear configuración extendida con dirección y estado de configuración
            const configWithAll = {
              ...response.config!,
              addresses: address ? [address] : [],
              address: address ?? undefined,
              configuration_status: configStatus,
              client_name: address?.name ?? undefined,
              industry_id: address?.industry_id ?? undefined,
              client_type: address?.client_type ?? undefined,
              is_fixed: address?.is_fixed ?? false,
              shipping_prefill_address: address?.shipping_prefill_address ?? true,
            };

            clientConfigService.setConfig(configWithAll);
            clientContextPersistence.set(slug, configWithAll);

            // Manifest PWA dinámico por cliente: el backend arma name/theme/icons
            // a partir del WebConfig del slug y fija start_url/scope al cliente actual.
            manifestService.setClientManifest(slug);

            loaderService.setLoading(false);

            // Registrar la visita (fire-and-forget, nunca bloquea la navegación)
            pageVisitTracking.recordPageVisit(slug, clientId, state.url);

            return true;
          }),
        );
      } else {
        clientContextPersistence.clear();
        loaderService.setLoading(false);
        router.navigate(['']);
        return of(false);
      }
    }),
    catchError((error: HttpErrorResponse) => {
      if (error.status === 404) {
        logger.warn(`Client slug "${slug}" not found (404):`, error);
      } else {
        // Error transitorio (red, timeout, 5xx) tras agotar reintentos: no es evidencia
        // de que el cliente no exista, pero sin config no hay página que renderizar.
        logger.error('Unexpected error validating web config:', error);
      }
      clientContextPersistence.clear();
      loaderService.setLoading(false);
      router.navigate(['']);
      return of(false);
    }),
  );
};
